import { useMemo } from "react";
import * as THREE from "three";
import { project, EXCLUDED_STATES } from "../lib/projection";
import usStatesData from "../data/us-states.json";

// Builds a floating museum-display map of the continental US: bone-white filled
// landmass with crisp dark-gray state borders. Geometry is bundled (no fetch)
// so it always renders — including in embed views and offline.
export function USMap() {
  const features = usStatesData.features;

  const { lineGeometry, fillGeometry } = useMemo(() => {
    if (!features) return { lineGeometry: null, fillGeometry: null };
    const linePos = [];
    const fillPos = [];

    const ringToVecs = (ring) => {
      const pts = ring.map(([lng, lat]) => {
        const [x, z] = project(lng, lat);
        return new THREE.Vector2(x, z);
      });
      if (pts.length > 1) {
        const a = pts[0];
        const b = pts[pts.length - 1];
        if (a.x === b.x && a.y === b.y) pts.pop();
      }
      return pts;
    };

    const addOutline = (ring) => {
      for (let i = 0; i < ring.length - 1; i++) {
        const [ax, az] = project(ring[i][0], ring[i][1]);
        const [bx, bz] = project(ring[i + 1][0], ring[i + 1][1]);
        linePos.push(ax, 0.05, az, bx, 0.05, bz);
      }
    };

    const addFill = (rings) => {
      const contour = ringToVecs(rings[0]);
      if (contour.length < 3) return;
      const holes = rings.slice(1).map(ringToVecs);
      let faces;
      try {
        faces = THREE.ShapeUtils.triangulateShape(contour, holes);
      } catch (e) {
        return;
      }
      const all = contour.concat(...holes);
      for (const f of faces) {
        for (const idx of f) {
          const v = all[idx];
          if (v) fillPos.push(v.x, 0.0, v.y);
        }
      }
    };

    for (const f of features) {
      if (EXCLUDED_STATES.has(f.properties?.name)) continue;
      const g = f.geometry;
      if (!g) continue;
      if (g.type === "Polygon") {
        g.coordinates.forEach(addOutline);
        addFill(g.coordinates);
      } else if (g.type === "MultiPolygon") {
        g.coordinates.forEach((poly) => {
          poly.forEach(addOutline);
          addFill(poly);
        });
      }
    }

    const lg = new THREE.BufferGeometry();
    lg.setAttribute("position", new THREE.Float32BufferAttribute(linePos, 3));
    const fg = new THREE.BufferGeometry();
    fg.setAttribute("position", new THREE.Float32BufferAttribute(fillPos, 3));
    fg.computeVertexNormals();
    return { lineGeometry: lg, fillGeometry: fg };
  }, [features]);

  if (!lineGeometry) return null;

  return (
    <group>
      {/* Bone-white filled landmass */}
      <mesh geometry={fillGeometry} receiveShadow>
        <meshStandardMaterial
          color="#F1ECE1"
          roughness={0.96}
          metalness={0}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Crisp dark-gray state borders */}
      <lineSegments geometry={lineGeometry}>
        <lineBasicMaterial color="#3A3F47" transparent opacity={0.9} />
      </lineSegments>
    </group>
  );
}
